import { useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";
import { coverage } from "./coverageData";

const questions = [
  {
    id: "industry",
    q: "What does your business mostly do?",
    options: [
      { label: "Trade or manufacturing", hint: "goods in, goods out", covers: ["marine", "liability"] },
      { label: "Professional services", hint: "advice, tech, agencies", covers: ["liability", "cyber"] },
      { label: "Retail or jewellery", hint: "stores and high-value stock", covers: ["jewellers", "liability"] },
      { label: "Construction or projects", hint: "sites, plant, labour", covers: ["construction", "liability"] },
    ],
  },
  {
    id: "goods",
    q: "Do you ship or move goods?",
    options: [
      { label: "Yes, regularly", hint: "domestic or export", covers: ["marine"] },
      { label: "Rarely or never", hint: "we stay put", covers: [] },
    ],
  },
  {
    id: "data",
    q: "Do you store customer data or take online payments?",
    options: [
      { label: "Yes", hint: "CRM, website, UPI, cards", covers: ["cyber"] },
      { label: "No", hint: "mostly offline", covers: [] },
    ],
  },
  {
    id: "vehicles",
    q: "Do vehicles keep the business running?",
    options: [
      { label: "Yes, we run vehicles", hint: "even one counts", covers: ["fleet"] },
      { label: "No wheels", hint: "we’re stationary", covers: [] },
    ],
  },
  {
    id: "family",
    q: "And the people behind the business?",
    options: [
      { label: "Cover my family too", hint: "health, life, accident", covers: ["personal"] },
      { label: "Just the business", hint: "we’ll talk later", covers: [] },
    ],
  },
];

export default function RiskQuiz({ onConsult }) {
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState({});
  const done = step >= questions.length;

  const select = (questionId, option) => {
    setPicked({ ...picked, [questionId]: option });
    setTimeout(() => setStep(step + 1), 240);
  };

  const restart = () => {
    setPicked({});
    setStep(0);
  };

  const coverIds = [...new Set(questions.flatMap((question) => picked[question.id]?.covers || []))];
  const recommended = coverIds.map((id) => coverage.find((item) => item.id === id)).filter(Boolean);

  return (
    <section className="risk-quiz section-pad" id="risk-check" data-testid="risk-quiz-section">
      <div className="quiz-shell">
        <div className="quiz-left">
          <div className="section-kicker">60-second risk check</div>
          <h2>Where’s your<br /><em>cover gap?</em></h2>
          <p>Five quick taps. We’ll sketch the covers a business like yours usually needs — then a real advisor can confirm the rest.</p>
        </div>
        <div className="quiz-card" data-testid="quiz-card">
          {!done ? (
            <>
              <div className="quiz-progress">
                <span>Question 0{step + 1} / 0{questions.length}</span>
                <span>{Math.round((step / questions.length) * 100)}%</span>
              </div>
              <div className="quiz-question" data-testid={`quiz-question-${step + 1}`}>{questions[step].q}</div>
              <div className="quiz-options">
                {questions[step].options.map((option) => (
                  <button
                    className="quiz-option"
                    key={option.label}
                    onClick={() => select(questions[step].id, option)}
                    data-testid={`quiz-option-${questions[step].id}-${option.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span><b>{option.label}</b>{option.hint && <small>{option.hint}</small>}</span>
                    <ArrowRight size={15} />
                  </button>
                ))}
              </div>
              <div className="quiz-nav">
                {step > 0 ? (
                  <button className="quiz-text-btn" onClick={() => setStep(step - 1)} data-testid="quiz-back-button"><ArrowLeft size={14} /> Back</button>
                ) : <span />}
              </div>
            </>
          ) : (
            <div className="quiz-results" data-testid="quiz-results">
              <div className="quiz-progress"><span>Your likely cover stack</span></div>
              <h3>{recommended.length > 0 ? `${recommended.length} cover${recommended.length > 1 ? "s" : ""} to look at first.` : "A quick audit will find your gaps."}</h3>
              <p>This is a directional read, not advice — a proper risk audit confirms the details. Tap any cover to open its plain-English guide.</p>
              <div className="quiz-covers">
                {recommended.map(({ id, icon: Icon, label, vibe }) => (
                  <Link className="quiz-cover" to={`/coverage/${id}`} key={id} data-testid={`quiz-cover-link-${id}`}>
                    <Icon size={17} />
                    <span>{label}<small>{vibe}</small></span>
                    <ArrowRight size={14} />
                  </Link>
                ))}
              </div>
              <button className="primary-button full" onClick={onConsult} data-testid="quiz-consultation-button">Confirm with an advisor <ArrowRight size={16} /></button>
              <div className="quiz-nav">
                <button className="quiz-text-btn" onClick={restart} data-testid="quiz-restart-button"><RotateCcw size={13} /> Start over</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
